import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';

import formatReason from 'utils/formatReason';
import { WRITE_OFF_REASONS } from 'constants/reasons';
import { bookingShape } from 'constants/propTypes';
import Modal from 'components/ui/Modal';
import { ActionButton, ConfirmButton } from 'components/ui/Button';
import Select from 'components/ui/Select';

import { ButtonWrapper, StyledTextarea } from './ForcedCancellationModal.styles';
import {
  hasSegmentDepartedMoreThan60DaysAgo,
  getSegmentsForItemRefs,
  hasDBTicketsSelected
} from '../../../pages/BookingDetails/Journeys/AfterSalesCtas/AfterSalesCtas.utils';

const ForcedCancellationModal = ({ isOpen, isLoading, onToggle, onSubmit, selectedItemRefs, booking, mergeThirdPartyModal }) => {
  const { register, handleSubmit } = useForm();
  const segments = booking && Array.from(getSegmentsForItemRefs(booking, selectedItemRefs));
  const shouldShowWarning = hasSegmentDepartedMoreThan60DaysAgo(segments);
  const [showWarning, setShowWarning] = useState(shouldShowWarning);
  const hasDBTickets = hasDBTicketsSelected({ segments });

  const handleOnSubmit = ({ code, reason }) => {
    const [value, label] = code.split(`_`);

    onSubmit({
      overrideReason: formatReason(value, reason, label),
      overrideAftersalesRules: true
    });
  };

  return showWarning ? (
    <Modal isOpen={isOpen} title={<FormattedMessage id="outsideNormalReasonsForOverride.cancellationWarning" />} onClose={onToggle}>
      <div>
        <p>
          <FormattedMessage id="outsideNormalReasonsForOverride.overridingRulesWarning" />
        </p>
        <p>
          <FormattedMessage id="outsideNormalReasonsForOverride.overridingRulesWarningProceed" />
        </p>
      </div>

      {mergeThirdPartyModal && (
        <>
          <FormattedMessage id="manageBooking.cancelRefundThirdParty" />
          <p>
            <FormattedMessage id="manageBooking.cancelRefundThirdPartyMessage" />
          </p>
        </>
      )}

      <ButtonWrapper>
        <ActionButton
          type="button"
          data-cy="forced-cancellation-warning-confirm"
          data-testid="abandon-confirm-button-warning"
          onClick={() => setShowWarning(false)}
          disabled={isLoading}
        >
          <FormattedMessage id="outsideNormalReasonsForOverride.confirmCancellationWarning" />
        </ActionButton>

        <ActionButton
          type="button"
          data-cy="forced-cancellation-warning-abandon"
          data-testid="abandon-button-warning"
          onClick={onToggle}
          disabled={isLoading}
        >
          <FormattedMessage id="outsideNormalReasonsForOverride.abandonCancellationWarning" />
        </ActionButton>
      </ButtonWrapper>
    </Modal>
  ) : (
    <Modal isOpen={isOpen} title={<FormattedMessage id="manageBooking.forcedCancellation.modal.title" />} onClose={onToggle}>
      <form onSubmit={handleSubmit(handleOnSubmit)} data-cy="forced-cancellation-form" data-testid="forced-cancellation-form">
        {hasDBTickets ? (
          <FormattedMessage id="db.overrideNotPossible" />
        ) : (
          <>
            <p>
              <FormattedMessage id="manageBooking.forcedCancellation.modal.paragraph" />
            </p>
            <Select
              data-cy="code"
              data-testid="code"
              {...register('code', {
                required: true
              })}
            >
              {WRITE_OFF_REASONS.map(({ code, label }) => (
                <option key={code} value={`${code}_${label}`}>
                  {label}
                </option>
              ))}
            </Select>

            <h4>
              <FormattedMessage id="overrideFees.explanation" />
            </h4>

            <StyledTextarea
              required
              rows="3"
              cols="40"
              data-testid="reason"
              data-cy="reason"
              {...register('reason', {
                required: true,
                min: 1,
                max: 37
              })}
            />
          </>
        )}

        {mergeThirdPartyModal && (
          <>
            <FormattedMessage id="manageBooking.cancelRefundThirdParty" />
            <p>
              <FormattedMessage id="manageBooking.cancelRefundThirdPartyMessage" />
            </p>
          </>
        )}

        <ButtonWrapper>
          <ConfirmButton
            type="submit"
            data-cy="submit"
            data-testid="submit"
            loading={isLoading}
            disabled={isLoading || hasDBTickets}
            variant="primary"
          >
            <FormattedMessage id="refunds.confirmCancellation" />
          </ConfirmButton>

          <ActionButton
            type="button"
            data-cy="forced-cancellation-abandon"
            data-testid="abandon-button"
            onClick={onToggle}
            disabled={isLoading}
          >
            <FormattedMessage id="manageBooking.abandonCancellation" />
          </ActionButton>
        </ButtonWrapper>
      </form>
    </Modal>
  );
};

ForcedCancellationModal.propTypes = {
  isOpen: PropTypes.bool,
  isLoading: PropTypes.bool,
  onToggle: PropTypes.func,
  onSubmit: PropTypes.func,
  booking: bookingShape,
  selectedItemRefs: PropTypes.arrayOf(PropTypes.string),
  mergeThirdPartyModal: PropTypes.bool
};

export default ForcedCancellationModal;
